import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, CheckCheck } from 'lucide-react';
import { api } from '@/lib/api';
import { relativeTime } from '@/lib/relativeTime';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface Notification {
  id: string;
  type: string;
  title: string;
  body: string;
  link: string | null;
  isRead: boolean;
  createdAt: string;
}

interface NotificationPage {
  items: Notification[];
  meta: { unread: number; total: number };
}

export function NotificationBell() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  // The badge polls cheaply; the full list is only fetched when opened.
  const { data: count } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: async () => {
      const { data } = await api.get<{ unread: number }>('/notifications/unread-count');
      return data.unread;
    },
    refetchInterval: 60_000,
  });

  const { data } = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: async () => {
      const { data: result } = await api.get<NotificationPage>('/notifications', {
        params: { page: 1, pageSize: 15 },
      });
      return result;
    },
    enabled: open,
  });

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] }),
      queryClient.invalidateQueries({ queryKey: ['notifications', 'list'] }),
    ]);
  };

  const markRead = async (id: string) => {
    await api.post(`/notifications/${id}/read`).catch(() => undefined);
    await refresh();
  };

  const markAllRead = async () => {
    await api.post('/notifications/read-all').catch(() => undefined);
    await refresh();
  };

  const unread = count ?? 0;

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setOpen((v) => !v)}
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-expanded={open}
      >
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-800 px-1 text-[10px] font-semibold text-white">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </Button>

      {open && (
        <>
          {/* Click-away layer, so the panel closes without a global listener. */}
          <button
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setOpen(false)}
            aria-label="Close notifications"
            tabIndex={-1}
          />

          <div className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-xl border bg-popover shadow-lg sm:w-96">
            <div className="flex items-center justify-between border-b px-4 py-2.5">
              <p className="text-sm font-semibold">Notifications</p>
              {unread > 0 && (
                <Button variant="ghost" size="sm" onClick={() => void markAllRead()}>
                  <CheckCheck className="h-3.5 w-3.5" />
                  Mark all read
                </Button>
              )}
            </div>

            <div className="max-h-96 overflow-y-auto overscroll-y-contain scrollbar-thin">
              {!data ? (
                <p className="px-4 py-8 text-center text-sm text-muted-foreground">Loading…</p>
              ) : data.items.length === 0 ? (
                <p className="px-4 py-8 text-center text-sm text-muted-foreground">
                  Nothing yet. Course assignments, results and certificates appear here.
                </p>
              ) : (
                <ul className="divide-y">
                  {data.items.map((notification) => {
                    const content = (
                      <>
                        <div className="flex items-start gap-2">
                          {!notification.isRead && (
                            <span
                              className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-brand-800"
                              aria-label="Unread"
                            />
                          )}
                          <div className={cn('min-w-0 flex-1', notification.isRead && 'pl-4')}>
                            <p className="text-sm font-medium leading-snug">{notification.title}</p>
                            <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                              {notification.body}
                            </p>
                            <p className="mt-1 text-[11px] text-muted-foreground">
                              {relativeTime(notification.createdAt)}
                            </p>
                          </div>
                        </div>
                      </>
                    );

                    return (
                      <li key={notification.id}>
                        {notification.link ? (
                          <Link
                            to={notification.link}
                            onClick={() => {
                              setOpen(false);
                              void markRead(notification.id);
                            }}
                            className="block px-4 py-3 transition-colors hover:bg-muted/60"
                          >
                            {content}
                          </Link>
                        ) : (
                          <button
                            onClick={() => void markRead(notification.id)}
                            className="block w-full px-4 py-3 text-left transition-colors hover:bg-muted/60"
                          >
                            {content}
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
